/**
 * Language Service
 * 
 * API client for language management endpoints.
 */

import { apiClient } from './api.service';
import type {
  Language,
  CreateLanguageDto,
  UpdateLanguageDto,
  LanguageQueryParams,
  LanguagePaginatedResponse,
} from '@/types/language.types';

// ============================================================================
// LANGUAGE SERVICE
// ============================================================================

class LanguageService {
  private readonly basePath = '/languages';

  /**
   * Get all languages with pagination and filters
   */
  async getAll(
    params?: LanguageQueryParams,
    opts: { signal?: AbortSignal } = {},
  ): Promise<LanguagePaginatedResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.search) queryParams.set('search', params.search);

    const queryString = queryParams.toString();
    const endpoint = queryString ? `${this.basePath}?${queryString}` : this.basePath;

    const response = await apiClient.get<LanguagePaginatedResponse>(endpoint, {
      signal: opts.signal,
    });

    if (!response.success || !response.data) {
      throw new Error('Error al cargar idiomas');
    }

    return response.data;
  }

  /**
   * Get language by ID
   */
  async getById(
    id: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<Language> {
    const response = await apiClient.get<Language>(`${this.basePath}/${id}`, {
      signal: opts.signal,
    });

    if (!response.success || !response.data) {
      throw new Error('Idioma no encontrado');
    }

    return response.data;
  }

  /**
   * Get language by code
   */
  async getByCode(
    code: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<Language> {
    const response = await apiClient.get<Language>(
      `${this.basePath}/code/${code}`,
      { signal: opts.signal },
    );

    if (!response.success || !response.data) {
      throw new Error('Idioma no encontrado');
    }

    return response.data;
  }

  /**
   * Create a new language
   */
  async create(data: CreateLanguageDto): Promise<Language> {
    const response = await apiClient.post<Language>(this.basePath, data);
    
    if (!response.success || !response.data) {
      throw new Error('Error al crear idioma');
    }

    return response.data;
  }

  /**
   * Update an existing language
   */
  async update(id: string, data: UpdateLanguageDto): Promise<Language> {
    const response = await apiClient.patch<Language>(`${this.basePath}/${id}`, data);
    
    if (!response.success || !response.data) {
      throw new Error('Error al actualizar idioma');
    }

    return response.data;
  }



  /**
   * Delete a language
   */
  async delete(id: string): Promise<void> {
    const response = await apiClient.delete(`${this.basePath}/${id}`);
    
    if (!response.success) {
      throw new Error('Error al eliminar idioma');
    }
  }
}

export const languageService = new LanguageService();
